import { lazy, Suspense } from "react";
import { WATCH_PAGES } from "./data/watchPages";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PasswordGate } from "@/components/PasswordGate";
import { FeedbackWidget } from "@/components/staging/FeedbackWidget";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { ClientOnly } from "@/components/ClientOnly";
import { IS_STAGING } from "@/config/staging";

// Keep Index eager — it's the landing page
import Index from "./pages/Index";

// Lazy-load all other pages
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VideosHub = lazy(() => import("./pages/VideosHub"));
const WatchPage = lazy(() => import("./components/WatchPage").then((m) => ({ default: m.WatchPage })));
const Services = lazy(() => import("./pages/Services"));
const ConcreteServices = lazy(() => import("./pages/ConcreteServices"));
const PavingServices = lazy(() => import("./pages/PavingServices"));
const WaterproofingServices = lazy(() => import("./pages/WaterproofingServices"));

// Masonry service pages
const PaverPatios = lazy(() => import("./pages/masonry/PaverPatios"));
const RetainingWalls = lazy(() => import("./pages/masonry/RetainingWalls"));
const StepsStoops = lazy(() => import("./pages/masonry/StepsStoops"));
const OutdoorKitchens = lazy(() => import("./pages/masonry/OutdoorKitchens"));
const FireplacesFirepits = lazy(() => import("./pages/masonry/FireplacesFirepits"));
const FireplaceInstallation = lazy(() => import("./pages/masonry/FireplaceInstallation"));
const StoneVeneer = lazy(() => import("./pages/masonry/StoneVeneer"));
const MasonryRepair = lazy(() => import("./pages/masonry/Repair"));
const PaverPatioRepair = lazy(() => import("./pages/masonry/PaverPatioRepair"));
const PaverWalkways = lazy(() => import("./pages/masonry/PaverWalkways"));
const PaverDriveways = lazy(() => import("./pages/masonry/PaverDriveways"));
const RetainingWallRepair = lazy(() => import("./pages/masonry/RetainingWallRepair"));
const StoneMasonryInstallation = lazy(() => import("./pages/masonry/StoneMasonryInstallation"));
const StoneMasonryRepair = lazy(() => import("./pages/masonry/StoneMasonryRepair"));
const CustomMasonry = lazy(() => import("./pages/masonry/CustomMasonry"));
const BrickWork = lazy(() => import("./pages/masonry/BrickWork"));
const BlockWork = lazy(() => import("./pages/masonry/BlockWork"));
const CommercialMasonry = lazy(() => import("./pages/masonry/CommercialMasonry"));

// Concrete service pages
const ConcretePatios = lazy(() => import("./pages/concrete/Patios"));
const ConcreteDriveways = lazy(() => import("./pages/concrete/Driveways"));
const ConcreteSidewalks = lazy(() => import("./pages/concrete/Sidewalks"));
const StampedConcrete = lazy(() => import("./pages/concrete/StampedConcrete"));
const ConcreteRepair = lazy(() => import("./pages/concrete/Repair"));
const ConcreteFoundationRepair = lazy(() => import("./pages/concrete/FoundationRepair"));
const ConcreteFoundations = lazy(() => import("./pages/concrete/Foundations"));
const ConcreteFootings = lazy(() => import("./pages/concrete/Footings"));

// Waterproofing
const BasementWaterproofing = lazy(() => import("./pages/waterproofing/BasementWaterproofing"));
const PatioWaterproofing = lazy(() => import("./pages/waterproofing/PatioWaterproofing"));

// Location pages
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const PassaicCounty = lazy(() => import("./pages/locations/PassaicCounty"));
const BergenCounty = lazy(() => import("./pages/locations/BergenCounty"));
const EssexCounty = lazy(() => import("./pages/locations/EssexCounty"));
const MorrisCounty = lazy(() => import("./pages/locations/MorrisCounty"));
const Clifton = lazy(() => import("./pages/locations/Clifton"));
const PassaicCity = lazy(() => import("./pages/locations/Passaic"));
const PaverPatioPenningtonPark = lazy(() => import("./pages/locations/PaverPatioPenningtonPark"));
const PaverPatioDowntownPaterson = lazy(() => import("./pages/locations/PaverPatioDowntownPaterson"));

// Other pages
const FAQ = lazy(() => import("./pages/FAQ"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const DataRequest = lazy(() => import("./pages/DataRequest"));
const ProTips = lazy(() => import("./pages/ProTips"));
const ChoosingProfessionalMasonryCompany = lazy(() => import("./pages/pro-tips/ChoosingProfessionalMasonryCompany"));
const BenefitsOfPaverPatio = lazy(() => import("./pages/pro-tips/BenefitsOfPaverPatio"));
const WhatIsAStoop = lazy(() => import("./pages/pro-tips/WhatIsAStoop"));
const PaverPatioCostNorthJersey = lazy(() => import("./pages/pro-tips/PaverPatioCostNorthJersey"));

// Landing pages (not in SSG/sitemap)
const LPPaverPatios = lazy(() => import("./pages/lp/PaverPatios"));
const LPRetainingWalls = lazy(() => import("./pages/lp/RetainingWalls"));
const Financing = lazy(() => import("./pages/Financing"));
const Packages = lazy(() => import("./pages/Packages"));
const BackyardPromo = lazy(() => import("./pages/BackyardPromo"));
const BackyardBundleThankYou = lazy(() => import("./pages/BackyardBundleThankYou"));

const queryClient = new QueryClient();

const AppContent = () => (
  <>
    <ScrollToTop />
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        
        {/* Masonry Services */}
        <Route path="/paving-services/paver-patios" element={<PaverPatios />} />
        <Route path="/masonry-services/retaining-walls" element={<RetainingWalls />} />
        <Route path="/masonry-services/steps-stoops" element={<StepsStoops />} />
        <Route path="/masonry-services/outdoor-kitchens" element={<OutdoorKitchens />} />
        <Route path="/masonry-services/fire-pits" element={<FireplacesFirepits />} />
        <Route path="/masonry-services/fireplace-installation" element={<FireplaceInstallation />} />
        <Route path="/masonry-services/stone-veneer" element={<StoneVeneer />} />
        <Route path="/masonry-services/steps-stoops-repair" element={<MasonryRepair />} />
        
        <Route path="/paving-services/paver-patio-repair" element={<PaverPatioRepair />} />
        <Route path="/paving-services/paver-walkways" element={<PaverWalkways />} />
        <Route path="/paving-services/paver-driveways" element={<PaverDriveways />} />
        <Route path="/masonry-services/retaining-wall-repair" element={<RetainingWallRepair />} />
        <Route path="/masonry-services/stone-masonry" element={<StoneMasonryInstallation />} />
        <Route path="/masonry-services/stone-masonry-repair" element={<StoneMasonryRepair />} />
        <Route path="/masonry-services/custom-masonry" element={<CustomMasonry />} />
        <Route path="/masonry-services/brick-work" element={<BrickWork />} />
        <Route path="/masonry-services/block-work" element={<BlockWork />} />
        <Route path="/masonry-services/commercial-masonry" element={<CommercialMasonry />} />
        <Route path="/paving-services" element={<PavingServices />} />
        {/* Concrete Services */}
        <Route path="/concrete-services" element={<ConcreteServices />} />
        <Route path="/concrete-services/patios" element={<ConcretePatios />} />
        <Route path="/concrete-services/driveways" element={<ConcreteDriveways />} />
        <Route path="/concrete-services/sidewalks" element={<ConcreteSidewalks />} />
        <Route path="/concrete-services/stamped-concrete" element={<StampedConcrete />} />
        <Route path="/concrete-services/repair" element={<ConcreteRepair />} />
        <Route path="/concrete-services/foundation-repair" element={<ConcreteFoundationRepair />} />
        <Route path="/concrete-services/foundations" element={<ConcreteFoundations />} />
        <Route path="/concrete-services/footings" element={<ConcreteFootings />} />
        
        {/* Location Pages */}
        <Route path="/service-areas" element={<ServiceAreas />} />
        
        <Route path="/service-areas/passaic-county" element={<PassaicCounty />} />
        <Route path="/service-areas/bergen-county" element={<BergenCounty />} />
        <Route path="/service-areas/essex-county" element={<EssexCounty />} />
        <Route path="/service-areas/morris-county" element={<MorrisCounty />} />
        <Route path="/service-areas/passaic-county/clifton" element={<Clifton />} />
        <Route path="/service-areas/passaic-county/passaic" element={<PassaicCity />} />
        <Route path="/service-areas/passaic-county/paver-patio-pennington-park-paterson" element={<PaverPatioPenningtonPark />} />
        <Route path="/service-areas/passaic-county/paver-patio-downtown-paterson" element={<PaverPatioDowntownPaterson />} />
        <Route path="/waterproofing-services" element={<WaterproofingServices />} />
        <Route path="/waterproofing-services/basement-waterproofing" element={<BasementWaterproofing />} />
        <Route path="/waterproofing-services/patio-waterproofing" element={<PatioWaterproofing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/videos" element={<VideosHub />} />
        {WATCH_PAGES.map((p) => (
          <Route key={p.slug} path={`/videos/${p.slug}`} element={<WatchPage slug={p.slug} />} />
        ))}
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/data-request" element={<DataRequest />} />
        
        {/* Pro Tips */}
        <Route path="/pro-tips" element={<ProTips />} />
        <Route path="/pro-tips/choosing-a-professional-masonry-company" element={<ChoosingProfessionalMasonryCompany />} />
        <Route path="/pro-tips/benefits-of-adding-a-paver-patio" element={<BenefitsOfPaverPatio />} />
        <Route path="/pro-tips/what-is-a-stoop" element={<WhatIsAStoop />} />
        <Route path="/pro-tips/paver-patio-cost-north-jersey" element={<PaverPatioCostNorthJersey />} />
        
        {/* Landing Pages */}
        <Route path="/lp/paver-patios" element={<LPPaverPatios />} />
        <Route path="/lp/retaining-walls" element={<LPRetainingWalls />} />
        <Route path="/financing" element={<Financing />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/lp/backyard-bundle" element={<BackyardPromo />} />
        <Route path="/lp/backyard-bundle/thank-you" element={<BackyardBundleThankYou />} />
        
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    {IS_STAGING && <FeedbackWidget />}
    <ClientOnly>
      <CookieConsentBanner />
    </ClientOnly>
  </>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          {IS_STAGING ? (
            <PasswordGate>
              <AppContent />
            </PasswordGate>
          ) : (
            <AppContent />
          )}
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
